// Copyright (c) 2026 Otyka LLC. All rights reserved.

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './SubmissionsTable.css';

const SENTIMENT_OPTIONS = [
  { label: 'All sentiments', value: '' },
  { label: 'Positive', value: 'POSITIVE' },
  { label: 'Critical', value: 'NEGATIVE' },
  { label: 'Mixed', value: 'MIXED' },
  { label: 'Neutral', value: 'NEUTRAL' },
];

export default function SubmissionsTable({ categoryFilter, sentimentMeta, apiBase, apiHeaders }) {
  const [sentiment, setSentiment] = useState('');
  const [items, setItems] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const fetchPage = useCallback(async (token = null, replace = true) => {
    if (replace) setLoading(true);
    else setLoadingMore(true);
    setError(null);

    try {
      const params = new URLSearchParams({ limit: 25 });
      if (categoryFilter) params.set('category', categoryFilter);
      if (sentiment) params.set('sentiment', sentiment);
      if (token) params.set('nextToken', token);

      const res = await axios.get(`${apiBase}/insights/submissions?${params}`, { headers: apiHeaders });
      const { items: newItems, nextToken: newToken } = res.data;

      setItems((prev) => replace ? newItems : [...prev, ...newItems]);
      setNextToken(newToken);
    } catch (err) {
      setError('Failed to load submissions.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [categoryFilter, sentiment]);

  useEffect(() => {
    fetchPage(null, true);
  }, [fetchPage]);

  function toggleRow(id) {
    setExpanded((prev) => (prev === id ? null : id));
  }

  return (
    <div className="submissions">
      <div className="submissions__toolbar">
        <h3 className="submissions__title">Individual Submissions</h3>
        <div className="submissions__filters">
          <select
            className="dashboard__select"
            value={sentiment}
            onChange={(e) => setSentiment(e.target.value)}
            aria-label="Filter by sentiment"
          >
            {SENTIMENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="submissions__error" role="alert">{error}</div>
      )}

      {loading ? (
        <div className="submissions__loading" aria-busy="true">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="submissions__skeleton-row" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="submissions__empty">
          <span aria-hidden="true">📭</span>
          <p>No submissions match the current filters.</p>
        </div>
      ) : (
        <>
          <div className="submissions__table-wrap">
            <table className="submissions__table" aria-label="Feedback submissions">
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Category</th>
                  <th scope="col">Sentiment</th>
                  <th scope="col">Summary</th>
                  <th scope="col" aria-label="Investment areas count">Areas</th>
                  <th scope="col"><span className="sr-only">Details</span></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const isOpen = expanded === item.feedbackId;
                  const sentMeta = sentimentMeta[item.sentiment] || sentimentMeta.NEUTRAL;
                  return (
                    <React.Fragment key={item.feedbackId}>
                      <tr
                        className={`submissions__row${isOpen ? ' submissions__row--open' : ''}`}
                        onClick={() => toggleRow(item.feedbackId)}
                        aria-expanded={isOpen}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="submissions__date" data-label="Date">
                          {formatDate(item.submittedAt)}
                        </td>
                        <td data-label="Category">
                          <span className="submissions__category-badge">
                            {item.category}
                          </span>
                        </td>
                        <td data-label="Sentiment">
                          <span
                            className="submissions__sentiment-badge"
                            style={{ background: sentMeta.bg, color: sentMeta.color }}
                          >
                            {sentMeta.label}
                          </span>
                        </td>
                        <td className="submissions__summary" data-label="Summary">
                          {item.summary || <span className="submissions__no-val">—</span>}
                        </td>
                        <td className="submissions__areas-count" data-label="Areas">
                          {(item.investmentAreas || []).length}
                        </td>
                        <td className="submissions__chevron" aria-hidden="true">
                          {isOpen ? '▲' : '▼'}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="submissions__detail-row" aria-label="Submission detail">
                          <td colSpan={6} className="submissions__detail-cell">
                            <div className="submissions__detail">
                              {item.investmentAreas?.length > 0 && (
                                <div className="submissions__detail-section">
                                  <p className="submissions__detail-label">Investment Areas</p>
                                  <div className="submissions__area-chips">
                                    {item.investmentAreas.map((ia, i) => (
                                      <span
                                        key={i}
                                        className={`submissions__area-chip submissions__area-chip--${ia.priority?.toLowerCase() || 'low'}`}
                                        title={ia.description}
                                      >
                                        {ia.area}
                                        {ia.priority && (
                                          <span className="submissions__area-chip-pri">
                                            {ia.priority[0]}
                                          </span>
                                        )}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {item.transcript && (
                                <div className="submissions__detail-section">
                                  <p className="submissions__detail-label">Transcript</p>
                                  <p className="submissions__transcript">{item.transcript}</p>
                                </div>
                              )}
                              <p className="submissions__feedback-id">ID: {item.feedbackId}</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {nextToken && (
            <div className="submissions__load-more">
              <button
                className="btn btn--secondary"
                onClick={() => fetchPage(nextToken, false)}
                disabled={loadingMore}
                aria-label="Load more submissions"
              >
                {loadingMore ? (
                  <><span className="btn__spinner" aria-hidden="true" /> Loading…</>
                ) : (
                  'Load more'
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
